import type { SortDirection } from '@shared/types/sort.types';

import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltMenu, CobaltMenuItem } from '@cae/cobalt-react/components/menu';
import { CobaltPagination } from '@cae/cobalt-react/components/pagination';
import { CobaltRadioButton } from '@cae/cobalt-react/components/radio-button';
import { CobaltTableContainer } from '@cae/cobalt-react/components/table';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { CobaltIconSortableOutline } from '@cae/cobalt-react/icons/outline/CobaltIconSortableOutline';
import { InstructorsTile } from '@features/instructors/components/tile/InstructorsTile';
import { useInstructorsData } from '@features/instructors/contexts/data/useInstructorsData';
import * as React from 'react';

import styles from './InstructorsList.module.css';

const InstructorsList = React.memo((): React.ReactElement => {
  const {
    instructors,
    pageSize,
    pageIndex,
    sortBy,
    sortDirection,
    handlePageIndexChange,
    handlePageSizeChange,
    handleSortChange,
  } = useInstructorsData();
  const totalItems = instructors.data.total;
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const sortTriggerRef = React.useRef<HTMLDivElement>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = React.useState<boolean>(false);

  const handleSortMenuToggle = React.useCallback((): void => {
    setIsSortMenuOpen(prev => !prev);
  }, []);

  const handleSortMenuClose = React.useCallback((): void => {
    setIsSortMenuOpen(false);
  }, []);

  const handleSelectSortDirection = React.useCallback(
    (direction: SortDirection): void => {
      handleSortChange(sortBy, direction);
      setIsSortMenuOpen(false);
    },
    [handleSortChange, sortBy]
  );

  const pagination = (
    <CobaltPagination
      length={totalItems}
      pageSize={pageSize}
      pageIndex={pageIndex}
      onPageIndexChange={handlePageIndexChange}
      onPageSizeChange={handlePageSizeChange}
      className={styles.instructorsListPagination}
      hasItemsPerPage={false}
    />
  );

  return (
    <CobaltTableContainer
      className={styles.instructorsList}
      data-testid="instructors-list"
      pagination={!isMobile ? pagination : undefined}
    >
      <div className={styles.instructorsListHeader}>
        <h2 className="heading-01">Instructors</h2>
        <div ref={sortTriggerRef} className={styles.instructorsListSortWrapper}>
          <CobaltButton
            id="instructors-list-sort-button"
            variant="ghost"
            onClick={handleSortMenuToggle}
            aria-label="Sort by name"
            aria-haspopup="menu"
            aria-expanded={isSortMenuOpen}
            data-testid="instructors-list-sort-button"
          >
            <CobaltIconSortableOutline aria-hidden="true" />
            Sort
          </CobaltButton>
          <CobaltMenu
            isOpen={isSortMenuOpen}
            anchorEl={sortTriggerRef.current}
            position="bottom-end"
            onClose={handleSortMenuClose}
            aria-labelledby="instructors-list-sort-button"
          >
            <CobaltMenuItem
              onClick={() => handleSelectSortDirection('asc')}
              className={styles.instructorsListSortMenuItem}
              data-testid="instructors-list-sort-option-asc"
            >
              <CobaltRadioButton
                name="instructors-sort-direction"
                value="asc"
                checked={sortDirection === 'asc'}
                readOnly
                labelText="A to Z"
                tabIndex={-1}
              />
            </CobaltMenuItem>
            <CobaltMenuItem
              onClick={() => handleSelectSortDirection('desc')}
              className={styles.instructorsListSortMenuItem}
              data-testid="instructors-list-sort-option-desc"
            >
              <CobaltRadioButton
                name="instructors-sort-direction"
                value="desc"
                checked={sortDirection === 'desc'}
                readOnly
                labelText="Z to A"
                tabIndex={-1}
              />
            </CobaltMenuItem>
          </CobaltMenu>
        </div>
      </div>
      <div
        className={styles.instructorsListGrid}
        data-testid="instructors-list-grid"
      >
        {instructors.data.elements.map(instructor => (
          <InstructorsTile
            instructor={instructor}
            key={instructor.instructorId}
            shouldRedirectToDetailPanel
            clickable
          />
        ))}
      </div>
      {isMobile && pagination}
    </CobaltTableContainer>
  );
});

InstructorsList.displayName = 'InstructorsList';
export { InstructorsList };
